import { Box, Button, Chip, InputBase } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { LockOutlined } from '@mui/icons-material';
import {
    NotificationOrigin,
    NotificationType,
    getNotificationOriginTranslationId,
    getNotificationTypeTranslationId,
} from '@synergy/models';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { PaloColumn, PaloListView } from './palo-list-view';
import { PaloConfirmationDialog } from './palo-confirmation-dialog';

const c = tokens.color;

/** One "send this document there" rule, as both portals receive it. */
export interface PaloNotificationSetting {
    id?: number;
    notificationOrigin: NotificationOrigin;
    /** Empty: the document goes to whoever it goes to by default. */
    recipientEmail: string | null;
    notificationType?: NotificationType | null;
    /** Kept by POOLALPIN; the portal shows it and the server refuses changes. */
    isLocked?: boolean;
}

/** A kind of document the portal lists, with what it is called there. */
export interface PaloNotificationDocument {
    origin: NotificationOrigin;
    /** Defaults to the name of the origin. */
    title?: ReactNode;
    /** When the mail goes out — "nach Abschluss bzw. Freigabe". */
    hint?: ReactNode;
    /** Who the mail goes to when no address is set, without "(Standard)". */
    standard: ReactNode;
    /** Listed only where an entry exists — set up by POOLALPIN, not offered. */
    hidden?: boolean;
}

export interface PaloNotificationSettingsProps {
    title: ReactNode;
    documents: PaloNotificationDocument[];
    settings: PaloNotificationSetting[];
    onAdd: (origin: NotificationOrigin, email: string | null) => void;
    onRemove: (id: number) => void;
}

/**
 * The domains POOLALPIN sends from. Addresses there are set up by POOLALPIN
 * and only by POOLALPIN (GF 15.10.2026); the server holds the same rule.
 */
const POOLALPIN_ADDRESS = /@(poolalpin|pool-alpin)\.com$/i;

const normalize = (email?: string | null): string => (email ?? '').trim().toLowerCase();

const isPoolalpinAddress = (email?: string | null): boolean => POOLALPIN_ADDRESS.test(normalize(email));

/**
 * The address the document is kept to from POOLALPIN's master data — the
 * invoice address, which goes to the accounting with every invoice. Further
 * addresses are a copy beside it.
 */
const fromMasterData = (settings: PaloNotificationSetting[]): boolean =>
    settings.some(setting => setting.isLocked && !isPoolalpinAddress(setting.recipientEmail));

type AddressError = 'invalid' | 'poolalpin' | 'duplicate';

interface DocumentRow extends PaloNotificationDocument {
    settings: PaloNotificationSetting[];
}

/** The attachments a document goes out with, once each. */
const attachmentsOf = (row: DocumentRow): NotificationType[] => [
    ...new Set(
        row.settings.map(setting => setting.notificationType).filter((type): type is NotificationType => !!type)
    ),
];

/**
 * Who gets which document by mail, one row per kind of document (#2284).
 *
 * The rules used to be a list of entries to open and edit, where an entry
 * without an address read as an empty one — and deleting it switched the mail
 * off for that document without saying so. Each document now names its
 * recipients side by side: the default one in blue and by name, the added
 * addresses beside it, and what POOLALPIN set up locked.
 *
 * Shared by the member and the supplier portal; they differ only in which
 * documents they list and who the default recipient is.
 */
export const PaloNotificationSettings = (props: PaloNotificationSettingsProps): ReactElement => {
    const intl = useIntl();
    const [addingTo, setAddingTo] = useState<NotificationOrigin | null>(null);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState<AddressError | null>(null);
    const [lastOne, setLastOne] = useState<{ row: DocumentRow; id: number } | null>(null);

    const rows = useMemo<DocumentRow[]>(() => {
        const listed = props.documents.map(document => ({
            ...document,
            settings: props.settings
                .filter(setting => setting.notificationOrigin === document.origin)
                // The default recipient first, as it is the one the mail goes to anyway.
                .sort((a, b) => Number(!!a.recipientEmail) - Number(!!b.recipientEmail)),
        }));

        return listed.filter(row => !row.hidden || row.settings.length > 0);
    }, [props.documents, props.settings]);

    const documentName = (row: DocumentRow): ReactNode =>
        row.title ?? <FormattedMessage id={getNotificationOriginTranslationId(row.origin)} />;

    const startAdding = (origin: NotificationOrigin): void => {
        setAddingTo(origin);
        setDraft('');
        setError(null);
    };

    const add = (row: DocumentRow): void => {
        const email = normalize(draft);
        const problem: AddressError | null = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
            ? 'invalid'
            : isPoolalpinAddress(email)
            ? 'poolalpin'
            : row.settings.some(setting => normalize(setting.recipientEmail) === email)
            ? 'duplicate'
            : null;

        setError(problem);

        if (null === problem) {
            props.onAdd(row.origin, email);
            setAddingTo(null);
        }
    };

    const remove = (row: DocumentRow, id: number): void => {
        // Without a recipient the document is no longer sent at all.
        if (1 === row.settings.length) {
            setLastOne({ row, id });

            return;
        }

        props.onRemove(id);
    };

    const recipient = (row: DocumentRow, setting: PaloNotificationSetting): ReactElement => {
        const isDefault = !setting.recipientEmail;
        const fromPoolalpin = isPoolalpinAddress(setting.recipientEmail);
        // Where the recipients of a document get different attachments, each
        // says its own; the column alone could not tell whose is whose (R-156).
        const ownAttachment = attachmentsOf(row).length > 1 && setting.notificationType && (
            <Box component="span" sx={{ color: c.ink3 }}>
                {'\u00a0· '}
                <FormattedMessage id={getNotificationTypeTranslationId(setting.notificationType)} />
            </Box>
        );

        const label = isDefault ? (
            <>
                {row.standard} <FormattedMessage id="app.notifications.palo.standard" defaultMessage="(Standard)" />
                {ownAttachment}
            </>
        ) : (
            <>
                {setting.recipientEmail}
                {setting.isLocked && (
                    <Box component="span" sx={{ color: c.ink3 }}>
                        {'\u00a0· '}
                        {fromPoolalpin ? (
                            <FormattedMessage
                                id="app.notifications.palo.lockedPoolalpin"
                                defaultMessage="von POOLALPIN eingerichtet"
                            />
                        ) : (
                            <FormattedMessage
                                id="app.notifications.palo.lockedMasterData"
                                defaultMessage="aus den Stammdaten bei POOLALPIN"
                            />
                        )}
                    </Box>
                )}
                {ownAttachment}
            </>
        );

        return (
            <Chip
                key={setting.id}
                size="small"
                label={label}
                icon={setting.isLocked ? <LockOutlined /> : undefined}
                onDelete={setting.isLocked || !setting.id ? undefined : () => remove(row, setting.id as number)}
                sx={{
                    height: 24,
                    fontSize: '12.5px',
                    fontWeight: 400,
                    // The dot the theme puts before a chip label belongs to the
                    // status scale; a recipient is not a state.
                    '& .MuiChip-label::before': { display: 'none' },
                    '& .MuiChip-icon': { fontSize: 14, color: c.ink3, marginLeft: '8px' },
                    '& .MuiChip-deleteIcon': { fontSize: 15 },
                    ...(isDefault
                        ? { background: c.accentSoft, border: `1px solid ${c.accentLine}`, color: c.accentHi }
                        : setting.isLocked
                        ? { background: c.surface2, border: `1px dashed ${c.lineHard}`, color: c.ink2 }
                        : { background: c.surface, border: `1px solid ${c.line}`, color: c.ink }),
                }}
            />
        );
    };

    const addressField = (row: DocumentRow): ReactElement => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <InputBase
                autoFocus
                type="email"
                value={draft}
                placeholder={intl.formatMessage({
                    id: 'app.notifications.palo.placeholder',
                    defaultMessage: 'name@betrieb.at',
                })}
                onChange={event => {
                    setDraft(event.target.value);
                    setError(null);
                }}
                onKeyDown={event => {
                    if ('Enter' === event.key) {
                        add(row);
                    }

                    if ('Escape' === event.key) {
                        setAddingTo(null);
                    }
                }}
                sx={{
                    width: 240,
                    height: 26,
                    fontSize: '12.5px',
                    padding: '0 8px',
                    border: `1px solid ${error ? c.crit : c.lineHard}`,
                    borderRadius: `${tokens.radius.small}px`,
                    background: c.surface,
                }}
            />
            <Button size="small" variant="contained" onClick={() => add(row)}>
                <FormattedMessage id="app.common.add" defaultMessage="Hinzufügen" />
            </Button>
            <Button size="small" variant="text" color="inherit" onClick={() => setAddingTo(null)}>
                <FormattedMessage id="app.common.cancel" defaultMessage="Abbrechen" />
            </Button>
            {error && (
                <Box component="span" sx={{ color: c.crit, fontSize: '12px' }}>
                    {'invalid' === error && (
                        <FormattedMessage
                            id="app.notifications.palo.error.invalid"
                            defaultMessage="Bitte eine gültige E-Mail-Adresse eingeben."
                        />
                    )}
                    {'poolalpin' === error && (
                        <FormattedMessage
                            id="app.notifications.palo.error.poolalpin"
                            defaultMessage="POOLALPIN-Adressen richtet nur POOLALPIN ein."
                        />
                    )}
                    {'duplicate' === error && (
                        <FormattedMessage
                            id="app.notifications.palo.error.duplicate"
                            defaultMessage="Diese Adresse ist für diesen Beleg schon eingetragen."
                        />
                    )}
                </Box>
            )}
        </Box>
    );

    const columns: PaloColumn<DocumentRow>[] = [
        {
            key: 'document',
            head: <FormattedMessage id="app.notifications.palo.document" defaultMessage="Beleg" />,
            // Name and hint of the document; the recipients take the rest.
            width: '22%',
            wrap: true,
            cell: row => (
                <Box sx={{ padding: '6px 0' }}>
                    <Box sx={{ fontWeight: 620, color: c.ink }}>{documentName(row)}</Box>
                    {row.hint && <Box sx={{ fontSize: '12px', color: c.ink3 }}>{row.hint}</Box>}
                </Box>
            ),
        },
        {
            key: 'recipients',
            type: 'name',
            head: <FormattedMessage id="app.notifications.palo.recipients" defaultMessage="Empfänger" />,
            wrap: true,
            cell: row => {
                const keptToMasterData = fromMasterData(row.settings);
                const hasDefault = row.settings.some(setting => !setting.recipientEmail);

                return (
                    <Box sx={{ padding: '6px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                            {0 === row.settings.length && (
                                <Box component="span" sx={{ color: c.warnInk, fontWeight: 560 }}>
                                    <FormattedMessage
                                        id="app.notifications.palo.noDispatch"
                                        defaultMessage="Kein Versand"
                                    />
                                </Box>
                            )}
                            {row.settings.map(setting => recipient(row, setting))}
                            {!hasDefault && !keptToMasterData && (
                                <Button size="small" variant="text" onClick={() => props.onAdd(row.origin, null)}>
                                    <FormattedMessage
                                        id="app.notifications.palo.addStandard"
                                        defaultMessage="+ Standard-Empfänger"
                                    />
                                </Button>
                            )}
                        </Box>
                        {keptToMasterData && (
                            <Box sx={{ fontSize: '12px', color: c.ink3 }}>
                                <FormattedMessage
                                    id="app.notifications.palo.masterDataNote"
                                    defaultMessage="Änderung der Rechnungsadresse bitte an POOLALPIN – sie wird mit jeder Rechnung an die Buchhaltung übergeben."
                                />
                            </Box>
                        )}
                        {addingTo === row.origin && addressField(row)}
                    </Box>
                );
            },
        },
        {
            key: 'attachment',
            type: 'classification',
            head: <FormattedMessage id="app.notifications.palo.attachment" defaultMessage="Anhang" />,
            // Set by POOLALPIN; the portal only says what comes along.
            cell: row => {
                const types = attachmentsOf(row);

                if (types.length > 1) {
                    return (
                        <FormattedMessage
                            id="app.notifications.palo.attachmentPerRecipient"
                            defaultMessage="je Empfänger"
                        />
                    );
                }

                return 1 === types.length ? <FormattedMessage id={getNotificationTypeTranslationId(types[0])} /> : '–';
            },
        },
        {
            key: 'add',
            align: 'right',
            // Wide enough for "+ Adresse hinzufügen" on one line.
            width: '190px',
            head: '',
            cell: row =>
                addingTo === row.origin ? null : (
                    <Button size="small" variant="text" onClick={() => startAdding(row.origin)}>
                        {fromMasterData(row.settings) ? (
                            <FormattedMessage id="app.notifications.palo.addCopy" defaultMessage="+ Kopie an …" />
                        ) : (
                            <FormattedMessage
                                id="app.notifications.palo.addAddress"
                                defaultMessage="+ Adresse hinzufügen"
                            />
                        )}
                    </Button>
                ),
        },
    ];

    return (
        <PaloListView<DocumentRow, string>
            title={props.title}
            columns={columns}
            items={rows}
            getRowId={row => row.origin}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
            tabs={
                <Box
                    sx={{
                        margin: '4px 0 12px',
                        padding: '10px 14px',
                        background: c.surface,
                        border: `1px solid ${c.line}`,
                        borderRadius: `${tokens.radius.default}px`,
                        fontSize: '13px',
                        color: c.ink2,
                    }}
                >
                    <FormattedMessage
                        id="app.notifications.palo.explanation"
                        defaultMessage="<b>Wer bekommt welchen Beleg per E-Mail?</b> Ohne Änderung geht jeder Beleg an den <b>Standard-Empfänger</b> (blau). Weitere Adressen bekommen eine eigene E-Mail mit demselben Beleg. Wird der Standard-Empfänger entfernt, bekommt nur noch die eingetragene Adresse den Beleg."
                        values={{ b: (chunks: ReactNode) => <strong style={{ color: c.ink }}>{chunks}</strong> }}
                    />
                </Box>
            }
        >
            <Box sx={{ marginTop: '10px', fontSize: '12px', color: c.ink3 }}>
                <FormattedMessage
                    id="app.notifications.palo.footer"
                    defaultMessage="Nur für Admins · Änderungen gelten für diesen Betrieb · jede Adresse nur einmal je Beleg"
                />
            </Box>
            <PaloConfirmationDialog
                isVisible={!!lastOne}
                title={
                    <FormattedMessage
                        id="app.notifications.palo.removeLast.title"
                        defaultMessage="Letzten Empfänger entfernen?"
                    />
                }
                action={<FormattedMessage id="app.notifications.palo.removeLast.action" defaultMessage="Entfernen" />}
                onCancel={() => setLastOne(null)}
                onConfirm={() => {
                    if (lastOne) {
                        props.onRemove(lastOne.id);
                    }

                    setLastOne(null);
                }}
            >
                {lastOne && (
                    <FormattedMessage
                        id="app.notifications.palo.removeLast.text"
                        defaultMessage="Danach bekommt niemand mehr „{document}“ per E-Mail."
                        values={{ document: documentName(lastOne.row) }}
                    />
                )}
            </PaloConfirmationDialog>
        </PaloListView>
    );
};
